import { action, computed, makeAutoObservable, observable, toJS } from 'mobx';
import global from 'global';

import { StoreBaseMethods } from './storeBase';
import { loadDataHelper } from './storeHelpers';
import { toLocalTime } from 'env/utils/dateUtil';
import { getBookingToSubmit } from './bookingsStore';
import { isServer } from 'env/ssr/ServerSideRenderManager';
import { analyticsService } from 'env/analytics/analyticsService';
import { getErrorDescription } from 'ui/_pages/bookings/BookingErrorCodes';
import { mergeEventProducts } from './mergeEventProducts';

class CheckoutStore {
  host = null;
  constructor({ data, host, authTokens } = {}) {
    StoreBaseMethods.copyData(data, this);

    this.host = host || data?.host;
    this.authTokens = authTokens;
    makeAutoObservable(this, undefined, { autoBind: true });
  }

  hasLoadedFromLocalStorage = false;
  loadFromLocalStorage() {
    if (!isServer() && window.localStorage) {
      this.basket = JSON.parse(window.localStorage.getItem('nx-basket')) || [];
      this.hasLoadedFromLocalStorage = true;
    }
  }

  saveBasket() {
    if (!isServer() && window.localStorage)
      window.localStorage.setItem(
        'nx-basket',
        JSON.stringify(toJS(this.basket)),
      );
  }

  getBasketForCheckout = () => {
    return this.basket.reduce((acc, item) => {
      if (item.type === 'course') {
        const course = {
          type: item.type,
          Course: {
            Id: item.data.Id,
          },
        };

        acc.push(course);
      }

      if (item.type === 'event') {
        item.data.EventProducts.forEach((product) => {
          if (product.Quantity > 0) {
            product.Attendees.forEach((att) => {
              acc.push({
                Type: item.type,
                EventAttendee: {
                  FullName: att.FullName,
                  Email: att.Email,
                  CalendarEventId: item.data.Id,
                  EventProductId: product.Id,
                },
                EventName: item.data.Name,
                Price: item.data.MostExpensivePrice,
                Currency: 'GBP',
              });
            });
          }
        });
      }

      if (item.type === 'contract') {
        const contract = {
          type: item.type,
          CoworkerContract: {
            StartDate: toLocalTime(item.data.startDate),
            TariffId: item.data.tariff.Id,
          },
        };

        acc.push(contract);
      }

      if (item.type === 'product') {
        const product = {
          type: item.type,
          CoworkerProduct: {
            ProductId: item.data.Id,
            Quantity: item.data.Quantity,
            RegularCharge: item.data.RegularCharge,
          },
        };

        acc.push(product);
      }

      if (item.type === 'booking') {
        const bookingToSubmit = getBookingToSubmit(item.data);
        const booking = {
          Type: item.type,
          Booking: {
            FromTime: bookingToSubmit.booking.FromTime,
            ToTime: bookingToSubmit.booking.ToTime,
            ResourceId: item.data.ResourceId,
            FloorPlanDeskId: item.data.FloorPlanDeskId,
            DiscountCode: item.data.DiscountCode,
            Notes: item.data.Notes,
            BookingVisitors: item.data.BookingVisitors,
            IncludeZoomInvite: item.data.IncludeZoomInvite,
            Tentative: item.data.Tentative,
            BookingProducts: [],
          },
        };

        item.data.ResourceProducts.forEach((product) => {
          if (product.Quantity > 0) {
            booking.Booking.BookingProducts.push({
              ProductId: product.ProductId,
              Quantity: product.Quantity,
            });
          }
        });
        acc.push(booking);
      }

      return acc;
    }, []);
  };

  @observable isLoadingBasketInvoice = false;
  @observable hasLoadedBasketInvoice = false;
  @observable basketInvoice = [];
  @action loadBasketInvoice({
    proposalId,
    createZeroValueInvoice = true,
  } = {}) {
    const basket = this.getBasketForCheckout();

    return loadDataHelper({
      store: this,
      agentKey: 'Basket',
      key: 'BasketInvoice',
      params: {
        basket,
        proposalId,
        createZeroValueInvoice,
        discountCode: this.discountCode,
      },
    }).then((invoice) => {
      if (invoice == null || !invoice.Message) {
        analyticsService.checkoutBasket({
          value: this.calculatedBasketPrice,
          currency: this.analyticsBasketDetails.currency,
          items: this.analyticsBasketDetails.items,
          coupon: this.discountCode,
        });
      }
      return invoice;
    });
  }

  @observable isLoadingPostItems = false;
  @observable hasLoadedPostItems = false;
  @observable postItems = [];
  @action loadPostItems({ proposalId } = {}) {
    const basket = this.getBasketForCheckout();

    return loadDataHelper({
      store: this,
      agentKey: 'Basket',
      key: 'PostItems',
      params: {
        basket,
        proposalId,
        discountCode: this.discountCode,
      },
    }).then((invoice) => {
      if (invoice != null && !invoice.Message) {
        analyticsService.checkoutBasket({
          value: this.calculatedBasketPrice,
          currency: this.analyticsBasketDetails.currency,
          items: this.analyticsBasketDetails.items,
          coupon: this.discountCode,
          invoice,
        });
        return invoice;
      }
    });
  }

  @action
  clearInvoicePreview() {
    this.invoicePreview = null;
  }

  @observable isLoadingInvoicePreview = false;
  @observable hasLoadedInvoicePreview = false;
  @observable invoicePreview = null;
  @action loadInvoicePreview(
    createZeroValueInvoice,
    { addNewContracts = false, proposalId = null } = {},
  ) {
    this.invoicePreview = null;
    const basket = this.getBasketForCheckout();

    analyticsService.viewBasket({
      value: this.calculatedBasketPrice,
      currency: this.analyticsBasketDetails.currency,
      items: this.analyticsBasketDetails.items,
    });

    return loadDataHelper({
      store: this,
      agentKey: 'Basket',
      key: 'InvoicePreview',
      params: {
        proposalId,
        addNewContracts,
        basket,
        createZeroValueInvoice,
        discountCode: this.discountCode,
      },
    });
  }

  @action loadPartialInvoicePreview({
    addNewContracts = false,
    proposalId = null,
    basket = [],
  } = {}) {
    this.invoicePreview = null;

    return loadDataHelper({
      store: this,
      agentKey: 'Basket',
      key: 'InvoicePreview',
      params: {
        proposalId,
        addNewContracts,
        basket,
        discountCode: this.discountCode,
      },
    });
  }

  @observable discountCode = null;
  @action applyDiscount(discountCode) {
    this.discountCode = discountCode;
  }

  @observable basket = [];
  @action addToBasket = ({
    type,
    data,
    bookingPrice,
    resourceProducts,
    previewInvoice = true,
  }) => {
    if (type == 'booking') {
      if (bookingPrice && bookingPrice.Currency) {
        data.BookingPrice = bookingPrice.PriceDecimal;
        data.BookingPriceCurrencyCode = bookingPrice.Currency.Code;
        data.BookingPriceMessage = bookingPrice.Resource?.ErrorCode
          ? getErrorDescription(
              bookingPrice.Resource.ErrorCode,
              bookingPrice.Message,
              global.t,
            )
          : bookingPrice.Message;
      }
      data.ResourceProducts = resourceProducts || [];
      data.BookingVisitors = data.BookingVisitors || [];
      data.Id = data.Id || this.basket.length + 1;
      data.inBasket = true;

      analyticsService.addToBasket({
        value: data.BookingPrice,
        currency: data.BookingPriceCurrencyCode,
        items: [
          {
            item_id: data.ResourceId,
            item_name: data.ResourceName,
            item_category: type,
            price: data.BookingPrice,
            quantity: data.Quantity || 1,
          },
        ],
        basketDetails: this.analyticsBasketDetails,
      });
    }

    if (type == 'event') {
      analyticsService.addToBasket({
        value: data.MostExpensivePrice,
        currency: 'GBP',
        items: [
          {
            item_id: data.Id,
            item_name: data.Name,
            item_category: type,
            price: data.MostExpensivePrice,
            quantity: data.Quantity || 1,
          },
        ],
        basketDetails: this.analyticsBasketDetails,
      });
    }

    const foundItem = this.basket.find(
      (i) => i.type === type && i.data.Id == data.Id,
    );
    if (foundItem) {
      if (type === 'event') {
        data.EventProducts = mergeEventProducts(
          data.EventProducts,
          foundItem.data.EventProducts,
        );
      }
      foundItem.data = data;
    } else {
      this.basket.push({
        type,
        data,
      });
    }

    if (previewInvoice) this.loadInvoicePreview();

    this.saveBasket();
  };

  @action removeFromBasket({ item, previewInvoice }) {
    analyticsService.removeFromBasket({
      value: item.data.BookingPrice,
      currency: item.data.BookingPriceCurrencyCode,
      items: [
        {
          item_id: item.data.ResourceId,
          item_name: item.data.ResourceName,
          price: item.data.BookingPrice,
          item_category: item.type,
        },
      ],
      basketDetails: this.analyticsBasketDetails,
    });

    this.basket.remove(item);

    if (previewInvoice) this.loadInvoicePreview();

    this.saveBasket();
  }

  @action clearBasket() {
    analyticsService.clearBasket({
      value: this.calculatedBasketPrice,
      currency: this.analyticsBasketDetails.currency,
      items: this.analyticsBasketDetails.items,
    });

    this.basket = [];
    this.saveBasket();
  }

  @computed get calculatedBasketPrice() {
    return this.basket.reduce(
      (prev, item) =>
        item.type == 'event'
          ? prev + item.data.MostExpensivePrice
          : prev + item.data.BookingPrice,
      0,
    );
  }

  @computed get analyticsBasketDetails() {
    const items = this.basket.map((item) => {
      if (item.type == 'event') {
        return {
          item_id: item.data.Id,
          item_name: item.data.Name,
          price: item.data.MostExpensivePrice,
          item_category: item.type,
          quantity: item.data.Quantity || 1,
        };
      }

      return {
        item_id: item.data.ResourceId,
        item_name: item.data.ResourceName,
        price: item.data.BookingPrice,
        item_category: item.type,
        quantity: item.data.Quantity || 1,
      };
    });
    const basketItem = this.basket.find((item) => item);

    return {
      items,
      currency: basketItem?.data.BookingPriceCurrencyCode || 'GBP',
    };
  }

  @observable pastBaskets = {};

  @action savePastBasket(invoiceSummary) {
    const items = toJS(this.basket);
    const discountCode = this.discountCode ?? null;

    const key = crypto.randomUUID();
    this.pastBaskets[key] = { items, invoiceSummary, discountCode };
    return key;
  }

  getPastBasket(key) {
    return this.pastBaskets[key] || null;
  }

  @computed get hasPlan() {
    return this.selectedPlans.length > 0;
  }

  @computed get selectedPlans() {
    return this.basket
      .filter((bi) => bi.type == 'contract')
      .map((bi) => bi.data.tariff.Id);
  }
}

export default CheckoutStore;
